// src/private/src/pages/app/clients/components/FunnelCard.jsx
import React, { memo } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const FUNNEL_STAGES = [
  { key: 'leads', label: 'Leads', color: '#2196f3' },
  { key: 'booked', label: 'Booked', color: '#7c3aed' },
  { key: 'issues', label: 'Issues', color: '#f59e0b' },
  { key: 'demos', label: 'Demos', color: '#f97316' },
  { key: 'netSold', label: 'Net Sold', color: '#22c55e' },
];

/**
 * Horizontal funnel visualization showing lead flow from Leads to Net Sold.
 * Each stage shows the count and a drop-off percentage from the previous stage.
 * Bar widths are proportional to volume.
 */
function FunnelCard({ leads = 0, booked = 0, issues = 0, demos = 0, netSold = 0 }) {
  const values = { leads, booked, issues, demos, netSold };
  const maxVal = Math.max(leads, booked, issues, demos, netSold, 1);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
        Lead Funnel
      </Typography>
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        spacing={0.5}
        sx={{ width: '100%', height: 120 }}
      >
        {FUNNEL_STAGES.map((stage, idx) => {
          const val = values[stage.key] || 0;
          const prevVal = idx > 0 ? values[FUNNEL_STAGES[idx - 1].key] || 0 : 0;
          const dropOff = idx > 0 && prevVal > 0
            ? ((val / prevVal) * 100).toFixed(0)
            : null;
          const barHeight = maxVal > 0 ? Math.max((val / maxVal) * 80, 8) : 8;

          return (
            <React.Fragment key={stage.key}>
              {idx > 0 && (
                <Stack alignItems="center" sx={{ minWidth: 28, flex: '0 0 auto' }}>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  {dropOff !== null && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: Number(dropOff) >= 50 ? 'success.main' : Number(dropOff) >= 25 ? 'warning.main' : 'error.main',
                      }}
                    >
                      {dropOff}%
                    </Typography>
                  )}
                </Stack>
              )}
              <Stack
                alignItems="center"
                spacing={0.5}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {val.toLocaleString('en-US')}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: barHeight,
                    borderRadius: 1,
                    bgcolor: stage.color,
                    opacity: 0.85,
                    transition: 'height 300ms ease',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                  noWrap
                >
                  {stage.label}
                </Typography>
              </Stack>
            </React.Fragment>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default memo(FunnelCard);
